import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Gift, RefreshCw } from 'lucide-react';
import type { LeaderboardPeriod } from '../../shared/periods.ts';
import type { LeaderboardEntry, LeaderboardResponse } from '../../shared/protocol.ts';
import { useAuth } from '../app/auth.ts';
import { useT } from '../app/preferences.ts';
import { Link } from '../components/Link.tsx';
import { PlayerAvatar, PlayerName } from '../components/PlayerLook.tsx';
import { Segmented } from '../components/Segmented.tsx';
import { fetchLeaderboard } from '../online/progressionApi.ts';
import { readStorage, writeStorage } from '../lib/storage.ts';

const TAB_KEY = 'outchess:leaderboard-tab';
const MEDALS = ['🥇', '🥈', '🥉'];

export function LeaderboardPage() {
  const t = useT();
  const { user, canSignIn, signIn } = useAuth();
  const [period, setPeriod] = useState<LeaderboardPeriod>(() => readStorage<LeaderboardPeriod>(TAB_KEY, 'day'));
  const [data, setData] = useState<{ period: LeaderboardPeriod; board: LeaderboardResponse } | { period: LeaderboardPeriod; error: true } | null>(null);

  const load = useCallback((p: LeaderboardPeriod) => {
    fetchLeaderboard(p)
      .then((board) => setData({ period: p, board }))
      .catch(() => setData({ period: p, error: true }));
  }, []);

  useEffect(() => {
    writeStorage(TAB_KEY, period);
    load(period);
  }, [period, load]);

  const current = data && data.period === period ? data : null;

  return (
    <div className="page-fill contained">
      <div className="page-top">
        <Link href="/" className="back-link">
          <ArrowLeft size={22} strokeWidth={2.5} /> {t('nav.home')}
        </Link>
        <h1 className="page-title">🏆 {t('board.title')}</h1>
      </div>

      <div className="board-layout">
        <Segmented<LeaderboardPeriod>
          name="board-period"
          label={t('board.title')}
          value={period}
          onChange={setPeriod}
          options={[
            { value: 'day', label: t('board.day') },
            { value: 'week', label: t('board.week') },
            { value: 'all', label: t('board.all') },
          ]}
        />

        {period !== 'all' && current && 'board' in current && current.board.end && (
          <div className="banner board-prize">
            <Gift size={22} />
            <span>{t('board.prizes', { period: t(`board.prizes.${period}`) })}</span>
            <Countdown end={current.board.end} />
          </div>
        )}

        {!current ? (
          <div className="center-state" role="status">
            <span className="spinner" />
            <p>{t('board.loading')}</p>
          </div>
        ) : 'error' in current ? (
          <div className="center-state">
            <p>{t('board.error')}</p>
            <button type="button" className="btn" onClick={() => load(period)}>
              <RefreshCw size={20} /> {t('board.retry')}
            </button>
          </div>
        ) : current.board.entries.length === 0 ? (
          <div className="center-state">
            <span className="result-icon emoji" aria-hidden="true">
              🌱
            </span>
            <p>{t('board.empty')}</p>
          </div>
        ) : (
          <Board board={current.board} myName={user?.username ?? null} />
        )}

        {!user && canSignIn && (
          <div className="banner">
            <span>{t('board.signIn')}</span>
            <button type="button" className="btn btn-sm btn-blue" onClick={() => signIn('/leaderboard')}>
              {t('auth.signInShort')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Board({ board, myName }: { board: LeaderboardResponse; myName: string | null }) {
  const podium = board.entries.slice(0, 3);
  const rest = board.entries.slice(3);
  const meOutside = board.me && !board.entries.some((e) => e.username === board.me!.username) ? board.me : null;

  return (
    <div className="card board-card">
      <ol className="podium" aria-label="Top 3">
        {/* Visual order 2 · 1 · 3, like a real podium. */}
        {[podium[1], podium[0], podium[2]].map((entry) =>
          entry ? <PodiumSpot key={entry.username} entry={entry} period={board.period} mine={entry.username === myName} /> : null,
        )}
      </ol>
      {rest.length > 0 && (
        <ol className="board-list" start={4}>
          {rest.map((entry) => (
            <BoardRow key={entry.username} entry={entry} period={board.period} mine={entry.username === myName} />
          ))}
        </ol>
      )}
      {meOutside && (
        <ol className="board-list board-me" start={meOutside.rank}>
          <BoardRow entry={meOutside} period={board.period} mine />
        </ol>
      )}
    </div>
  );
}

function Score({ entry, period }: { entry: LeaderboardEntry; period: LeaderboardPeriod }) {
  const t = useT();
  if (period === 'all') return <strong className="board-score">🏆 {entry.rating}</strong>;
  const gain = entry.gain ?? 0;
  return (
    <strong className={`board-score ${gain > 0 ? 'up' : gain < 0 ? 'down' : ''}`} title={t('board.gain')}>
      {gain > 0 ? '+' : ''}
      {gain}
    </strong>
  );
}

function PodiumSpot({ entry, period, mine }: { entry: LeaderboardEntry; period: LeaderboardPeriod; mine: boolean }) {
  return (
    <li className={`podium-spot place-${entry.rank}${mine ? ' mine' : ''}`}>
      <Link href={`/u/${entry.username}`} className="podium-link">
        <span className="podium-medal emoji" aria-label={`#${entry.rank}`}>
          {MEDALS[entry.rank - 1]}
        </span>
        <PlayerAvatar look={entry.look} name={entry.username} size={entry.rank === 1 ? 'xl' : 'lg'} />
        <PlayerName name={entry.username} look={entry.look} />
        <Score entry={entry} period={period} />
        <span className="podium-block" aria-hidden="true">
          {entry.rank}
        </span>
      </Link>
    </li>
  );
}

function BoardRow({ entry, period, mine }: { entry: LeaderboardEntry; period: LeaderboardPeriod; mine: boolean }) {
  const t = useT();
  return (
    <li className={`board-row${mine ? ' mine' : ''}`}>
      <Link href={`/u/${entry.username}`} className="board-row-link">
        <span className="board-rank">{entry.rank}</span>
        <PlayerAvatar look={entry.look} name={entry.username} size="sm" />
        <span className="board-who">
          <PlayerName name={entry.username} look={entry.look} />
          {mine && <span className="badge badge-accent">{t('board.you')}</span>}
        </span>
        <span className="board-games">{t(entry.games === 1 ? 'board.game' : 'board.games', { count: entry.games })}</span>
        <Score entry={entry} period={period} />
      </Link>
    </li>
  );
}

function Countdown({ end }: { end: number }) {
  const t = useT();
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  const minutes = Math.max(0, Math.floor((end - now) / 60_000));
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  const time = d > 0 ? t('board.days', { d, h }) : t('board.hours', { h, m });
  return <span className="badge">{t('board.endsIn', { time })}</span>;
}
